import { useState } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { twJoin, twMerge } from 'tailwind-merge';

import ComponentGuard from '@/components/ui/custom/ComponentGuard';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import Skeleton from '@/components/ui/custom/Skeleton';
import Tag from '@/components/ui/custom/dataDisplay/Tag';
import { Tooltip } from '@/components/ui/custom/dataDisplay/Tooltip';
import { REQUEST_STALE_TIME_IN_MS } from '@/constants';
import useAuth from '@/data/hook/useAuth';
import { klassesQueryKeys, usersQueryKeys } from '@/data/services/querykeys';

import ScheduledLesson from '@/models/ScheduledLesson';
import { UserTypeEnum } from '@/models/User';
import ModalHandler, {
  ModalState,
} from '@/components/admin/calendar/ModalHandler';
import { ROUTES } from '@/utils/routes';
import { PERMISSIONS } from '@/utils/_deprecated/ScheduledLessonPermission';
import { AlterDatePill } from '@/components/admin/calendar/ManageScheduledLesson/AlterDatePill';
import { AttendancePill } from '@/components/admin/calendar/ManageScheduledLesson/AttendancePill';
import { ProgressPill } from '@/components/admin/calendar/ManageScheduledLesson/ProgressPill';
import { AnticipatePostponePill } from '@/components/admin/calendar/ManageScheduledLesson/AnticipatePostponePill';
import { TeacherPill } from '@/components/admin/calendar/ManageScheduledLesson/TeacherPill';
import { isTeacher } from '@/functions/auth';
import StudentsPopup from '../../../staff/StudentsPopup';
import { AlterLinkButton } from './AlterLinkButton';
import { ReportButton } from './ReportButton';
import { StatusIcon } from './StatusIcon';
import { EditButton } from './EditButton';
import SimpleTextIcon from '@/components/ui/custom/SimpleTextIcon';
import useManageScheduledLesson from '@/data/hook/useManageScheduledLesson';
import KlassGroupButton from './KlassGroupButton';
import { KlassConectaTag } from '@/components/ui/custom/ConectaTag';
import { formatLessonName, formatLessonNamePrefix } from '@/utils/lesson/name';

export type ManageScheduledLessonState = 'view' | 'manager';

export type ManageScheduledLessonProps = {
  scheduledLesson: ScheduledLesson;
  updateLessons?: () => Promise<void>;
  state?: ManageScheduledLessonState;
};

export type StudentProgress = {
  student: string;
  progress: number;
  activitiesNotDone: number;
  chaptersUnread: number;
  presence: null | boolean;
};

export default function ManageScheduledLesson(
  props: ManageScheduledLessonProps,
) {
  const {
    progress,
    lesson,
    klass: klassId,
    hasDone,
    datetime,
    presences,
    absences,
    url,
    isActive,
    teacher: teacherId,
  } = props.scheduledLesson;
  const { state = 'manager', updateLessons, scheduledLesson } = props;

  const { user } = useAuth();

  const { data: teacher, isInitialLoading: loadingTeacher } = useQuery({
    ...usersQueryKeys.get(teacherId),
    enabled: !!teacherId,
    staleTime: REQUEST_STALE_TIME_IN_MS,
  });

  const { data: klass, isInitialLoading: loadingKlass } = useQuery({
    ...klassesQueryKeys.get(klassId),
    enabled: !!klassId,
    staleTime: REQUEST_STALE_TIME_IN_MS,
  });

  const [editing, setEditing] = useState(false);

  const [modalState, setModalState] = useState<ModalState>(ModalState.CLOSED);

  const [t] = useTranslation('translation', {
    keyPrefix: 'common',
  });

  const {
    isStartingSoon,
    isPublished,
    editPermission,
    anticipatePermission,
    bankNotPublished,
    hasLesson,
  } = useManageScheduledLesson({
    scheduledLesson,
    klass,
    user,
    state,
  });

  const stateColor = hasDone
    ? 'border-success bg-success-content'
    : 'border-secondary bg-secondary-content';

  const disabledColor = isActive ? '' : 'opacity-20 pointer-events-none';

  const formatedName = formatLessonName(lesson, props.scheduledLesson);

  const formatedPrefix = formatLessonNamePrefix({
    lessonOrder: lesson?.order ?? props.scheduledLesson.order,
    scheduledLessonType: props.scheduledLesson.type,
  });

  const isManager = state === 'manager';

  const lessonName = isManager ? formatedName : formatedPrefix;

  const onChangeModal = (state: ModalState) => setModalState(state);

  const loading = loadingKlass;

  if (loading)
    return (
      <Skeleton
        testId="loading"
        className={twJoin(
          'w-full rounded-lg bg-primary-content',
          isManager ? 'min-h-20 h-20' : 'h-16 min-h-16',
        )}
      />
    );

  if (klass)
    return (
      <li
        data-testid="manageScheduledLesson"
        className={twJoin(
          'flex items-center border rounded-lg gap-1 w-full',
          stateColor,
          disabledColor,
          isManager ? 'p-3.5' : 'p-1.5',
        )}
      >
        <StatusIcon hasDone={hasDone} render={isManager} />

        <div className="flex flex-col gap-1.5 md:gap-1 w-full overflow-hidden">
          <div
            className={twMerge(
              'flex gap-x-2.5 gap-y-1 items-start justify-between flex-col overflow-hidden w-full h-full',
              isManager ? 'xl:flex-row xl:items-center' : 'xs:flex-row',
            )}
          >
            <SimpleTextIcon
              tooltipText={formatedName}
              testId="lessonLink"
              title={lessonName}
              href={
                lesson
                  ? ROUTES.COURSES.LESSON.INFO(
                      klass.coursePathSlug,
                      lesson.id,
                      klass.id,
                    )
                  : undefined
              }
              className={{
                text: 'capitalize',
                base: isManager ? 'xl:w-full' : 'min-w-14 xs:self-center',
              }}
              tooltip
              truncate
            />

            <div
              className={twMerge(
                'gap-x-2.5 gap-y-0.5 flex items-start md:items-center overflow-hidden',
                isManager
                  ? 'xl:justify-end xl:w-full flex-col md:flex-row'
                  : 'grow justify-between grid grid-cols-3 xs:flex',
              )}
            >
              <div
                className={twJoin(
                  'flex gap-x-2.5 gap-y-1 overflow-hidden justify-start md:w-fit items-center',
                  isManager ? '' : 'col-span-2',
                )}
              >
                <SimpleTextIcon
                  testId="klassLink"
                  title={klass.name}
                  href={ROUTES.ADMIN.CLASSES.INFO({
                    name: klass.name.replace('#', ''),
                  })}
                  className={{
                    text: 'capitalize',
                  }}
                  tooltip
                  truncate
                />

                {/* TODO: release rendering when network can add class group link */}
                <ConditionalRenderer condition={false}>
                  <KlassGroupButton klassId={klassId} />
                </ConditionalRenderer>

                <KlassConectaTag klassType={klass.klassType} />
              </div>
              <div className="flex gap-x-1.5 gap-y-1 items-center justify-start shrink-0">
                <StudentsPopup
                  klass={klass}
                  scheduledLesson={scheduledLesson}
                  className={{ ul: state === 'view' ? 'max-w-[300px]' : '' }}
                />
                <AlterLinkButton
                  url={url ?? klass.classRoomUrl}
                  editing={editing}
                  render={!hasDone}
                  onChangeModal={onChangeModal}
                  state={state}
                />
                <EditButton
                  render={editPermission && !hasDone && isManager}
                  editing={editing}
                  onEditing={setEditing}
                />
              </div>
            </div>
          </div>

          <div className="flex justify-between items-center flex-wrap gap-x-2.5 gap-y-1">
            <AlterDatePill
              dateTime={moment(datetime).toDate()}
              klassDuration={klass?.duration}
              editing={editing}
              setModalState={() => setModalState(ModalState.KLASS_DATE)}
              editPermission={editPermission && !isTeacher(user?.userType)}
              state={state}
            />
            <ConditionalRenderer condition={hasDone}>
              <AttendancePill
                presences={presences}
                absences={absences}
                studentsCount={klass.enrollsCount}
                state={state}
              />

              <ConditionalRenderer condition={isManager}>
                <ProgressPill progress={progress} />
              </ConditionalRenderer>
            </ConditionalRenderer>

            <ConditionalRenderer condition={editing && !hasDone}>
              <div className="flex gap-2">
                <ComponentGuard
                  roles={[UserTypeEnum.SUPER_ADMIN, UserTypeEnum.UNIT_ADMIN]}
                >
                  <Tooltip
                    text={
                      !anticipatePermission ? t('doNotAnticipateLesson') : ''
                    }
                  >
                    <AnticipatePostponePill
                      state={ModalState.ANTICIPATE}
                      setModalState={() => setModalState(ModalState.ANTICIPATE)}
                      isDisable={!anticipatePermission}
                    />
                  </Tooltip>
                </ComponentGuard>

                <ComponentGuard
                  roles={[UserTypeEnum.SUPER_ADMIN, UserTypeEnum.UNIT_ADMIN]}
                >
                  <AnticipatePostponePill
                    state={ModalState.POSTPONE}
                    setModalState={() => setModalState(ModalState.POSTPONE)}
                    isDisable={false}
                  />
                </ComponentGuard>
              </div>
            </ConditionalRenderer>

            <ConditionalRenderer condition={isStartingSoon}>
              <Tag
                color="warning"
                className={{
                  base: twJoin(
                    'text-[0.875rem] gap-1 lowercase px-1',
                    isManager ? 'py-0.25' : 'py-0',
                  ),
                }}
              >
                {t('classStartsSoon')}
              </Tag>
            </ConditionalRenderer>

            <ReportButton
              state={state}
              render={!hasLesson || isPublished}
              klass={klass}
              scheduledLesson={props.scheduledLesson}
              disabled={
                !PERMISSIONS.SCHEDULED_LESSON_REPORT.ACCESS({
                  klass,
                  user,
                })
              }
            />

            <ConditionalRenderer condition={!hasLesson}>
              <Tag
                color="warning"
                className={{
                  base: twJoin(
                    'text-[0.875rem] gap-1 lowercase px-1',
                    isManager ? 'py-0.25' : 'py-0',
                  ),
                }}
              >
                {t('lessonNotAssigned')}
              </Tag>
            </ConditionalRenderer>

            <ConditionalRenderer
              condition={hasLesson && (!isPublished || bankNotPublished)}
            >
              <Tag
                color="warning"
                className={{
                  base: twJoin(
                    'text-[0.875rem] gap-1 lowercase px-1',
                    isManager ? 'py-0.25' : 'py-0',
                  ),
                }}
              >
                {t('lessonUnpublished')}
              </Tag>
            </ConditionalRenderer>

            <ConditionalRenderer condition={isManager}>
              <ComponentGuard
                roles={[UserTypeEnum.SUPER_ADMIN, UserTypeEnum.UNIT_ADMIN]}
              >
                <TeacherPill
                  loading={loadingTeacher}
                  teacher={teacher}
                  setModalState={() => setModalState(ModalState.TEACHER)}
                  editing={editing}
                  editPermission={editPermission}
                />
              </ComponentGuard>
            </ConditionalRenderer>
          </div>
        </div>

        <ComponentGuard
          roles={[
            UserTypeEnum.SUPER_ADMIN,
            UserTypeEnum.UNIT_ADMIN,
            UserTypeEnum.TEACHER,
          ]}
        >
          <ModalHandler
            updateLessons={updateLessons}
            scheduledLesson={props.scheduledLesson}
            klassInfo={klass}
            modalState={modalState}
            teacherInfo={teacher}
            closeModal={() => setModalState(ModalState.CLOSED)}
            redirectToModal={setModalState}
          />
        </ComponentGuard>
      </li>
    );

  return null;
}
